import { AxiosResponse } from 'axios';
import moment from 'moment';
import AES from 'crypto-js/aes';
import Utf8 from 'crypto-js/enc-utf8';
import { Config } from './config';
import { CustomeDropdownOptionType } from 'src/data-access/models/custom-types';
import { XLCellDataType, XLCellSymbolPosition } from 'src/data-access/models/enums';
import { IBBTableCellDTO, IBBTableHeaderDTO } from 'src/data-access/models/response/bbtable';
import { matchPath } from 'react-router-dom';

export type Browser = "Chrome" | "Edge" | "Firefox" | "Safari" | "Opera" | "Brave" | "IE" | "Unknown";

export const CommonHelper = {
    secretKey: Config.secret || "nrcglaz2dqtKbi5HRsvCJP08TfM4NxWIkYuZSE13GX6whB9pVyoLemF7DAQUOtjW",

    debounceTimeMS: 500,

    getDisplayDate: (utcDateTimeString: string | undefined | null) => utcDateTimeString ? moment.utc(utcDateTimeString).local().format("MM/DD/YYYY") : "",

    getDisplayDateTime: (utcDateTimeString: string | undefined | null, isIncludeSeconds: boolean = false) => utcDateTimeString ? moment.utc(utcDateTimeString).local().format(isIncludeSeconds ? "MM/DD/YYYY hh:mm:ss A" : "MM/DD/YYYY hh:mm A") : "",

    getDisplayDateTimeSmart: (
        utcDateTimeString: string | undefined | null,
        isIncludeSeconds: boolean = false
    ) => {
        if (!utcDateTimeString) return "";

        const m = moment.utc(utcDateTimeString).local();
        const now = moment();

        const isToday = m.isSame(now, "day");

        if (isToday) {
            return m.format(isIncludeSeconds ? "hh:mm:ss A" : "hh:mm A");
        } else {
            return m.format(isIncludeSeconds ? "MM/DD/YYYY hh:mm:ss A" : "MM/DD/YYYY hh:mm A");
        }
    },

    getDisplayDateTimeFormat: (utcDateTimeString: string | undefined | null, format: string) => utcDateTimeString ? moment.utc(utcDateTimeString).local().format(format) : "",

    getUtcDateTimeStrToMoment: (utcDateTimeString: string) => moment.utc(utcDateTimeString),

    getUtcDateTimeStrToMomentLocal: (utcDateTimeString: string) => moment.utc(utcDateTimeString).local(),

    getDateTimeDifference: (utcStartDateTimeString: string, utcEndDateTimeString?: string, diffUnit?: moment.unitOfTime.Diff) => {
        let a = moment.utc(utcStartDateTimeString).local();
        let b = moment();
        if (utcEndDateTimeString)
            b = moment.utc(utcEndDateTimeString).local();

        return a.diff(b, diffUnit);
    },

    getDateTimeNowMomentUTB: () => moment.utc(),

    getDateTimeNowMomentLocal: () => moment.utc().local(),

    getPrettyDateDifference: (date1UTCStr: string, date2UTCStr?: string | null) => {
        if (!date1UTCStr) return '';

        const m1 = moment.utc(date1UTCStr).startOf('day');
        let m2;
        if (date2UTCStr)
            m2 = moment.utc(date2UTCStr).startOf('day');
        else
            m2 = moment.utc().startOf('day');

        if (!m1.isValid() || !m2.isValid())
            return '';

        const duration = moment.duration(m2.diff(m1));

        const years = duration.years();
        const months = duration.months();
        const days = duration.days();

        let parts: string[] = [];

        if (years !== 0)
            parts.push(`${Math.abs(years)} year${Math.abs(years) !== 1 ? 's' : ''}`);
        if (months !== 0)
            parts.push(`${Math.abs(months)} month${Math.abs(months) !== 1 ? 's' : ''}`);
        if (days !== 0)
            parts.push(`${Math.abs(days)} day${Math.abs(days) !== 1 ? 's' : ''}`);
        if (parts.length === 0) {
            if (m1.isSame(m2, 'day'))
                return '0 day';
            else
                return '';
        }

        return parts.join(', ');
    },

    getUTCDateFromDate: (date: Date | null | undefined): Date | null => {
        if (!date || isNaN(date.getTime())) return null;

        return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    },

    formatDateToYYYYMMDD: (date: Date | null | undefined): string => {
        if (!date || isNaN(date.getTime())) return '';

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    },

    encrypt: (value: string) => {
        const ciphertext = AES.encrypt(value, CommonHelper.secretKey).toString();
        const urlSafe = ciphertext.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
        return urlSafe;
    },

    decrypt: (value: string) => {
        try {
            let base64 = value.replace(/-/g, '+').replace(/_/g, '/');
            const pad = base64.length % 4;
            if (pad) base64 += '='.repeat(4 - pad);

            const bytes = AES.decrypt(base64, CommonHelper.secretKey);
            const originalText = bytes.toString(Utf8);

            if (!originalText)
                throw new Error('Decryption failed. Invalid key or corrupted data.');

            return originalText;
        } catch (error) {
            console.error("Decryption Error:", error);
            return null;
        }
    },

    // sort list
    sortOptionsByLabel: (
        options: CustomeDropdownOptionType[],
        order: 'asc' | 'desc' = 'asc'
    ): CustomeDropdownOptionType[] => {
        return [...options].sort((a, b) => {
            const aLabel = typeof a.label === 'string' ? a.label : String(a.label);
            const bLabel = typeof b.label === 'string' ? b.label : String(b.label);

            return order === 'asc'
                ? aLabel.localeCompare(bLabel)
                : bLabel.localeCompare(aLabel);
        });
    },

    sortOptionsByLabelText: (
        options: CustomeDropdownOptionType[],
        order: 'asc' | 'desc' = 'asc'
    ): CustomeDropdownOptionType[] => {
        return [...options].sort((a, b) => {
            const aText = a.labelText ?? '';
            const bText = b.labelText ?? '';

            return order === 'asc'
                ? aText.localeCompare(bText)
                : bText.localeCompare(aText);
        });
    },

    sortByKey: <T>(data: T[], property: keyof T, order: 'asc' | 'desc' = 'asc'): T[] => {
        return [...data].sort((a, b) => {
            if (a[property]! < b[property]!) return order === 'asc' ? -1 : 1;
            if (a[property]! > b[property]!) return order === 'asc' ? 1 : -1;
            return 0;
        });
    },

    sortArray: (data: string[] | number[] | boolean[], order: 'asc' | 'desc' = 'asc'): any => {
        return data.sort((a: any, b: any) => {
            if (a < b) return order === 'asc' ? -1 : 1;
            if (a > b) return order === 'asc' ? 1 : -1;
            return 0;
        });
    },

    nl2br: (text: any) => {
        return text.replace(/\n/g, '<br />');
    },

    formatNumber: (num: string, decimalPalces: number = 2): string => {
        return (Math.round(parseFloat(num) * 100) / 100).toFixed(decimalPalces);
    },

    getAzurePublicFileURL: (filename: string): string => {
        return Config.publicContainerUrl + filename;
    },

    roundNumber: (num: string, places: number = 2): string => {
        return parseFloat(num).toFixed(places);
    },

    environment: {
        isLocal: (): boolean => Config.environment === "Local",
        isDevelopment: (): boolean => Config.environment === "Development",
        isProduction: (): boolean => Config.environment === "Production",
        isStaging: (): boolean => Config.environment === "Staging",
        isClientApp: (): boolean => Config.environment === "ClientApp",
        getName: (): string => Config.environment,
        getVersion: (): string => Config.version,
        getBuildDate: (): string => CommonHelper.getDisplayDateTime(Config.versionDate),
        getGitSha: (): string => Config.gitSha,
        getGitLastCommitAt: (): string => Config.gitLastCommitAt,
        getGitCommits: (): string => Config.gitCommits,
        getGitBranch: (): string => Config.gitBranch,
    },

    getAPIBaseUrl: (): string => Config.apiBaseUrl ?? "",

    getNotificationHubUrl: (): string => Config.notificationHubUrl ?? "",

    generateColorShades: (hex: string, percentages: number[]): string[] => {
        const hexToHSL = (hex: string): { h: number; s: number; l: number } => {
            let r = parseInt(hex.substring(1, 3), 16) / 255;
            let g = parseInt(hex.substring(3, 5), 16) / 255;
            let b = parseInt(hex.substring(5, 7), 16) / 255;

            let max = Math.max(r, g, b),
                min = Math.min(r, g, b);
            let h = 0,
                s,
                l = (max + min) / 2;

            if (max === min) {
                h = s = 0; // Achromatic
            } else {
                let d = max - min;
                s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
                switch (max) {
                    case r:
                        h = (g - b) / d + (g < b ? 6 : 0);
                        break;
                    case g:
                        h = (b - r) / d + 2;
                        break;
                    case b:
                        h = (r - g) / d + 4;
                        break;
                }
                h *= 60;
            }

            return { h, s: s * 100, l: l * 100 };
        };

        const hslToHex = (h: number, s: number, l: number): string => {
            s /= 100;
            l /= 100;

            const f = (n: number) => {
                const k = (n + h / 30) % 12;
                const a = s * Math.min(l, 1 - l);
                return Math.round((l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1))) * 255)
                    .toString(16)
                    .padStart(2, "0");
            };

            return `#${f(0)}${f(8)}${f(4)}`;
        };

        const { h, s, l } = hexToHSL(hex);
        const shades: string[] = [];

        percentages.forEach((percentage) => {
            let newL = l + (percentage / 100) * (percentage > 0 ? 100 - l : l);
            newL = Math.min(100, Math.max(0, newL)); // Ensure within range
            shades.push(hslToHex(h, s, newL));
        });

        return shades;
    },

    rgbStringToHex: (rgb: string): string => {
        const match = rgb.match(/\d+/g);
        if (!match || match.length !== 3) return "#000000";
        const [r, g, b] = match.map(Number);
        return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
    },

    msToPrettyString: (ms: number): string => {
        if (ms <= 0) return "0s";

        const seconds = Math.floor(ms / 1000) % 60;
        const minutes = Math.floor(ms / (1000 * 60)) % 60;
        const hours = Math.floor(ms / (1000 * 60 * 60));

        const parts = [];
        if (hours > 0) parts.push(`${hours}h`);
        if (minutes > 0) parts.push(`${minutes}m`);
        if (seconds > 0) parts.push(`${seconds}s`);

        return parts.join(" ");
    },

    jsonToFormData: (data: any, formData: FormData = new FormData(), parentKey?: string): FormData => {
        if (data === null || data === undefined) return formData;

        for (const key of Object.keys(data)) {
            if (key) {
                const value = data[key];
                const fullKey = parentKey ? `${parentKey}.${key}` : key;

                if (value instanceof File || value instanceof Blob)
                    formData.append(fullKey, value);

                else if (value instanceof FileList && value.length === 1)
                    formData.append(fullKey, value[0]);

                else if (value instanceof Date)
                    formData.append(fullKey, value.toISOString());

                else if (Array.isArray(value))
                    value.forEach((arrayItem, index) => {
                        const arrayKey = `${fullKey}[${index}]`;

                        if ((arrayItem instanceof File || arrayItem instanceof Blob))
                            formData.append(fullKey, arrayItem); //Here is the catch, don't use indexing for Array of Files

                        else if (typeof arrayItem === 'object' && arrayItem !== null && arrayItem !== undefined)
                            CommonHelper.jsonToFormData(arrayItem, formData, arrayKey);

                        else
                            formData.append(arrayKey, arrayItem);
                    });

                else if (typeof value === 'object' && value !== null)
                    CommonHelper.jsonToFormData(value, formData, fullKey); // recursive for nested objects

                else if (value !== undefined && value !== null)
                    formData.append(fullKey, String(value));
            }
        }

        return formData;
    },

    getCurrentBrowser: (userAgent: string = navigator.userAgent): Browser => {
        const nav = navigator as any;

        if (/Edg\/\d+/.test(userAgent)) return "Edge";
        if (/OPR\/|Opera/.test(userAgent)) return "Opera";
        if (typeof nav.brave === "object") return "Brave";
        if (/Chrome\/\d+/.test(userAgent) && !/Edg\/|OPR\//.test(userAgent)) return "Chrome";
        if (/Firefox\/\d+/.test(userAgent)) return "Firefox";
        if (/Safari\/\d+/.test(userAgent) && !/Chrome|Edg|OPR/.test(userAgent)) return "Safari";
        if (/MSIE |Trident\//.test(userAgent)) return "IE";

        return "Unknown";
    },

    getExtensionFromFilename: (filename?: string): string => {
        if (!filename) return "";
        const lastDotIndex = filename.lastIndexOf(".");
        return lastDotIndex !== -1 ? filename.slice(lastDotIndex + 1).toLowerCase() : "";
    },

    getFileIconUrl: (filename?: string): string => {
        if (!filename) return "/images/filetypes/file.svg";

        const extension = filename.split('.').pop()?.toLowerCase() ?? "";

        switch (extension) {
            case "ppt":
            case "pptx":
                return "/images/filetypes/ppt.svg";
            case "doc":
            case "docx":
                return "/images/filetypes/doc.svg";
            case "xls":
            case "xlsx":
            case "xlsm":
                return "/images/filetypes/xls.svg";
            case "pdf":
                return "/images/filetypes/pdf.svg";
            case "txt":
                return "/images/filetypes/txt.svg";
            case "jpg":
            case "jpeg":
                return "/images/filetypes/jpg.svg";
            case "png":
                return "/images/filetypes/png.svg";
            case "gif":
            case "bmp":
            case "svg":
                return "/images/filetypes/image.svg";
            case "zip":
                return "/images/filetypes/zip.svg";
            case "rar":
                return "/images/filetypes/zip1.svg";
            case "mp4":
                return "/images/filetypes/mp4.svg";
            default:
                return "/images/filetypes/file.svg"; // Default generic file icon
        }
    },

    getPrettyFileSize(bytes: number): string {
        if (bytes === 0) return "0 Bytes";
        const k = 1024;
        const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        const size = bytes / Math.pow(k, i);
        return `${size.toFixed(size < 10 ? 2 : 1)} ${sizes[i]}`;
    },

    getTrimmedString: (input?: string | null, length: number = 0): string => {
        const trimmedNullUndefinedSafeInput = (input ?? "").trim();
        if (length <= 0) return trimmedNullUndefinedSafeInput;
        else
            return trimmedNullUndefinedSafeInput.length > length ? trimmedNullUndefinedSafeInput.slice(0, length - 3) + "..." : trimmedNullUndefinedSafeInput;
    },

    generateRandomString: (length: number = 12): string => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        let result = '';
        const charsLength = chars.length;

        for (let i = 0; i < length; i++) {
            result += chars.charAt(Math.floor(Math.random() * charsLength));
        }

        return result;
    },

    generateUUID: (): string => {
        if (typeof crypto !== "undefined" && crypto.randomUUID) {
            return crypto.randomUUID();
        }

        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
            const r = crypto.getRandomValues(new Uint8Array(1))[0] & 15;
            const v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    },

    getInitials: (text?: string | null): string => {
        if (typeof text !== "string" || text === null || text.trim().length === 0)
            return "";

        const words = text.trim().split(/\s+/);

        if (words.length > 1)
            return words.slice(0, 2).map(word => word[0].toUpperCase()).join('');

        else if (text.length >= 2)
            return text.substring(0, 2).toUpperCase();

        else if (text.length === 1)
            return text[0].toUpperCase();

        return "";
    },

    nameToDarkColor: (name: string): string => {
        let hash = 0;
        for (let i = 0; i < name.length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }

        let color = '#';
        for (let i = 0; i < 3; i++) {
            // Restrict each RGB component to be between 0 and 160 to keep color dark
            const value = (hash >> (i * 8)) & 0xff;
            const darkValue = Math.floor((value % 160)); // 0–159
            color += darkValue.toString(16).padStart(2, '0');
        }

        return color;
    },

    getContrastTextColor: (bgColor: string): 'white' | 'black' => {
        // Remove "#" and parse RGB
        const r = parseInt(bgColor.substr(1, 2), 16);
        const g = parseInt(bgColor.substr(3, 2), 16);
        const b = parseInt(bgColor.substr(5, 2), 16);

        // Calculate brightness (YIQ formula)
        const yiq = (r * 299 + g * 587 + b * 114) / 1000;

        return yiq >= 128 ? 'black' : 'white';
    },

    maskNumericString: (cell: IBBTableCellDTO): string => {
        switch (cell.dataType) {
            case XLCellDataType.Percentage:
                return 'XX%';

            case XLCellDataType.Number:
                if (cell.isNegative) return '-XXXXXX';
                return 'XXXXXX';

            case "Currency":
                const symbol = cell.symbol || '$';
                const isSymbolPrefix = cell.symbolPosition === XLCellSymbolPosition.Prefix;
                if (isSymbolPrefix)
                    return `${symbol}XXXXXX`;
                return `XXXXXX${symbol}`;

            default:
                return 'XXXXXX';
        }
    },

    zeroNumericString: (input: string): string => {
        // Trim whitespace
        input = input.trim();

        // Handle percentage (ends with %)
        if (input.endsWith('%')) {
            return '0%';
        }

        // Regex to match patterns
        const currencyPrefixMatch = input.match(/^([$₹]|Rs\.?)/i); // Match $ or Rs. at start
        const currencySuffixMatch = input.match(/(USD)$/i);        // Match USD at end
        const decimalMatch = /\d+[\d,]*\.\d+/.test(input);          // Contains a decimal
        const numericPartMatch = input.match(/\d[\d,.]*/);         // Extract numeric part

        let masked = '';

        if (numericPartMatch) {
            if (decimalMatch)
                masked = '0.00';
            else
                masked = '0';
        }

        if (currencyPrefixMatch) {
            return `${currencyPrefixMatch[0]}${masked}`;
        } else if (currencySuffixMatch) {
            return `${masked} ${currencySuffixMatch[0]}`;
        } else {
            return masked;
        }
    },

    downloadFile: (
        response: AxiosResponse<Blob>,
        forceFilename?: string | null,
        suffix?: string | null
    ) => {
        debugger;
        if (!response.data) return;

        try {
            let finalFilename: string;

            if (forceFilename?.trim()) {
                // Use forceFilename exactly as given
                finalFilename = forceFilename.trim();
            } else {
                // Extract filename from Content-Disposition
                const disposition = response.headers?.['content-disposition'] || '';
                const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/i);
                const headerFilename = match ? match[1].replace(/['"]/g, '').trim() : "";
                const fallback = headerFilename || "download";

                // Extract base and extension
                const dotIndex = fallback.lastIndexOf(".");
                const base = dotIndex !== -1 ? fallback.substring(0, dotIndex) : fallback;
                const ext = dotIndex !== -1 ? fallback.substring(dotIndex + 1) : 'bin';

                // Add suffix and date
                const date = new Date();
                const formattedDate = `${(date.getMonth() + 1).toString().padStart(2, '0')}-${date
                    .getDate()
                    .toString()
                    .padStart(2, '0')}-${date.getFullYear()}`;

                finalFilename = `${base}${suffix ? `_${suffix}` : ''}_${formattedDate}.${ext}`;
            }

            // Trigger download
            const url = window.URL.createObjectURL(response.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = finalFilename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            a.remove();
        } catch (error) {
            console.error('Failed to download blob file:', error);
        }
    },

    downloadFileFromURL: (
        azurePublicDocPath: string,
        filename: string,
    ) => {
        if (!azurePublicDocPath) return;

        try {
            // Trigger download
            const url = CommonHelper.getAzurePublicFileURL(azurePublicDocPath);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
        } catch (error) {
            console.error('Failed to download blob file:', error);
        }
    },

    getTextAlignment: (cell?: IBBTableCellDTO | IBBTableHeaderDTO): "left" | "right" | "center" => {
        if (!cell) return "left";

        return cell.style.horizontalAlignment.toLowerCase() as ("left" | "right" | "center");
    },

    matchRoute: (routePattern: string, currentPath: string): boolean => {
        const guidRegex =
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89abAB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}$/;

        // First, check if route matches pattern
        const match = matchPath({ path: routePattern, end: true }, currentPath);
        if (!match) return false;

        // If no parameters in the route pattern, it’s a valid match
        if (!routePattern.includes(":")) return true;

        // Extract dynamic param names from route pattern
        const paramNames = (routePattern.match(/:([A-Za-z0-9_]+)/g) || []).map(p => p.substring(1));

        // Validate each dynamic param value against GUID regex
        for (const name of paramNames) {
            const value = match.params?.[name];
            if (!value || !guidRegex.test(value)) return false;
        }

        return true;
    },

    addCommasToNumber: (value: string | number | null | undefined): string => {
        if (value === null || value === undefined) return "";
        const num = typeof value === "number" ? value : parseFloat((value as string).replace(/,/g, ""));
        if (isNaN(num)) return value?.toString() ?? "";

        // Preserve decimals if present
        const [intPart, decimalPart] = num.toString().split(".");
        const formattedInt = parseInt(intPart, 10).toLocaleString("en-US");
        return decimalPart ? `${formattedInt}.${decimalPart}` : formattedInt;
    }
}